"use client";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useTransition, useState, useRef } from "react";
import { Spinner } from "../icons/icons";
import QRCode from "qrcode";
import QRCodeReact from "qrcode.react";
import { Turnstile } from "@marsidev/react-turnstile";
import { useTranslations } from "next-intl";

// Updated schema
const WhatsAppLinkSchema = z.object({
  whatsappNumber: z
    .string()
    .regex(/^\d+$/, "WhatsApp Number must contain only numbers")
    .min(1, "WhatsApp Number is required"),
  welcomeMessage: z.string().optional(),
});

export type FormInputs = z.infer<typeof WhatsAppLinkSchema>;

const WhatsAppLinkCreate = () => {
  const t = useTranslations("WhatsAppLinkCreateForm");
  const [isPending, startTransition] = useTransition();
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [shortenedLink, setShortenedLink] = useState<string | null>(null);
  const qrRef = useRef<HTMLDivElement>(null);

  const form = useForm<FormInputs>({
    resolver: zodResolver(WhatsAppLinkSchema),
    defaultValues: {
      whatsappNumber: "",
      welcomeMessage: "",
    },
  });

  async function shortenLink(longUrl: string) {
    try {
      const response = await fetch("/api/shorten-link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ long_url: longUrl }),
      });

      const data = await response.json();
      console.log("Shorten API Response:", data); // Debugging log

      if (!response.ok) {
        throw new Error(data.error || "Failed to shorten link");
      }

      return data.shortUrl;
    } catch (error) {
      console.error("Error shortening link:", error);
      toast.error(t("errorShorteningLink"));
      return longUrl;
    }
  }


  function onSubmit(values: FormInputs) {
    if (!turnstileToken) {
      toast.error(t("errorTurnstileToken"));
      return;
    }

    startTransition(async () => {
      const link = `https://wa.me/${values.whatsappNumber}?text=${encodeURIComponent(
        values.welcomeMessage || ""
      )}`;
      // Shorten the link
      const shortLink = await shortenLink(link);
      setGeneratedLink(link);
      setShortenedLink(shortLink);

      setGeneratedLink(link);
      toast.success((t("linkGenerated")));
      form.reset();
      setTurnstileToken(null); // Reset token after submission
    });
  }

  const handleCopyLink = () => {
    if (shortenedLink) {
      navigator.clipboard.writeText(shortenedLink);
      toast.success(t("linkCopied"));
    }
  };



  const handleDownloadQR = async () => {
    if (!shortenedLink) return;

    const now = new Date();
    const wibTime = new Date(now.getTime() + 7 * 60 * 60 * 1000);
    const timestamp = wibTime
      .toISOString()
      .replace("T", "_")
      .replace(/[:.]/g, "-")
      .split("Z")[0];

    // Membuat QR Code dengan ukuran besar menggunakan library `qrcode`
    try {
      const qrCanvas = document.createElement("canvas");
      await QRCode.toCanvas(qrCanvas, shortenedLink, {
        width: 1080, // Resolusi tinggi untuk QR Code
        errorCorrectionLevel: "H", // Tingkat koreksi kesalahan tinggi
      });

      const context = qrCanvas.getContext("2d");
      if (!context) return;

      // Tambahkan logo di tengah QR Code
      const logo = new Image();
      logo.src = "/qr-logo.png"; // Pastikan logo tersedia di path ini

      logo.onload = () => {
        const { width, height } = qrCanvas;
        const logoSize = width * 0.2; // Ukuran logo (20% dari ukuran QR Code)

        context.drawImage(logo, (width - logoSize) / 2, (height - logoSize) / 2, logoSize, logoSize);

        // Buat link untuk mendownload gambar
        const link = document.createElement("a");
        link.download = `qr-whatsapp-me-with-mubarokahdotcom-at-${timestamp}-WIB.png`;
        link.href = qrCanvas.toDataURL("image/png");
        link.click();
      };

      logo.onerror = () => {
        toast.error(t("errorQRLogo"));
      };
    } catch (error) {
      console.error("Error generating high-resolution QR Code:", error);
      toast.error(t("errorQRGeneration"));
    }
  };




  return (
    <div className="rounded-md bg-muted p-8 shadow-lg dark:bg-slate-800 dark:shadow-slate-850/20 border-solid border-2 border-primary">
      <h2 className="mb-8 text-lg font-bold">{t("title")}</h2>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="block space-y-5">
          <FormField
            control={form.control}
            name="whatsappNumber"
            render={({ field }) => (
              <FormItem className="space-y-0">
                <div className="relative mb-2">
                  <FormControl>
                    <Input
                      className="material-input rounded-none border-0 border-b-[1px] border-slate-300 bg-transparent px-0 dark:border-slate-600 dark:bg-transparent"
                      placeholder={t("placeholderPhoneNumber")}
                      required
                      {...field}
                    />
                  </FormControl>
                  <span className="material-input__underline"></span>
                </div>
                <FormMessage />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t("message1")}
                </p>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="welcomeMessage"
            render={({ field }) => (
              <FormItem className="relative space-y-0">
                <div className="relative mb-2">
                  <FormControl>
                    <Textarea
                      className="material-input rounded-none border-0 border-b-[1px] border-slate-300 bg-transparent px-0 dark:border-slate-600 dark:bg-transparent"
                      placeholder={t("placeholderWelcomeMessage")}
                      {...field}
                    />
                  </FormControl>
                  <span className="material-input__underline"></span>
                </div>
                <FormMessage />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t("message2")}
                </p>
              </FormItem>
            )}
          />

          {/* Turnstile CAPTCHA */}
          <div className="mt-4">
            <Turnstile
              siteKey="0x4AAAAAAA29-0PYk7u7JLd5"
              onSuccess={(token) => setTurnstileToken(token)}
            />
          </div>

          <Button type="submit" disabled={isPending} className="w-full">
            {isPending ? (
              <>
                <Spinner className="mr-2 h-5 w-5 animate-spin" />
                <span>{t("generatingLink")}</span>
              </>
            ) : (
              <span>{t("createLink")}</span>
            )}
          </Button>
        </form>
      </Form>

      {shortenedLink && (
        <div className="mt-8 p-4 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 hasil-link-wa">
          <h3 className="text-lg font-bold mb-2">{t("generatedLink")}</h3>
          <p className="mb-2"><a
            href={shortenedLink}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold"
          >{shortenedLink} | </a>
            <a
              href={shortenedLink}
              className="text-blue-500 underline text-base"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("openInWhatsApp")}
            </a>
          </p>

          <div ref={qrRef} className="mt-4">
            <QRCodeReact value={shortenedLink} size={128} />
          </div>
          <button
            onClick={handleCopyLink}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded"
          >
            {t("copyLink")}
          </button>
          <button onClick={handleDownloadQR} className="mt-4 ml-2 px-4 py-2 bg-blue-500 text-white rounded">
            {t("downloadQRCode")}
          </button>
        </div>
      )}
    </div>
  );
};

export default WhatsAppLinkCreate;

